import { IcoClose } from '@icons';
import { addUserDisclaimer } from '@lib/api_ts/usersApi';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { useHistory, useLocation } from 'react-router';

interface PrivacyPolicyProps {}

const PrivacyPolicy: React.FC<PrivacyPolicyProps> = () => {
	const location = useLocation();

	const needsAcceptanceByHash = location.hash === '#privacy-policy-to-accept';
	const needsAcceptanceByPath = location.pathname === '/privacy-policy-to-accept';

	const needsAcceptance = needsAcceptanceByHash || needsAcceptanceByPath;
	const isModalOpen = location.hash === '#privacy-policy' || needsAcceptance;
	const onHide = () => {
		needsAcceptanceByPath ? history.push('/') : history.push(location.pathname);
	};

	const [isAccepted, setIsAccepted] = useState(false);
	const history = useHistory();

	const queryClient = useQueryClient();

	const acceptPrivacyPolicyMutation = useMutation({
		mutationFn: () => addUserDisclaimer('privacy_policy'),
		onSuccess: () => {
			localStorage.setItem('has_accepted_privacy_policy', 'true');
			history.push('/projects');
			queryClient.invalidateQueries({ queryKey: ['userDisclaimers'] });
		},
	});

	const handleOnClick = () => {
		acceptPrivacyPolicyMutation.mutate();
	};

	return (
		<>
			<Button
				onClick={() => {
					history.push('#privacy-policy');
				}}
				className="btn custom-ghost-button"
				style={{
					color: '#666666',
					textTransform: 'none',
					fontWeight: 400,
					textDecoration: 'underline',
				}}
			>
				Privacy Policy
			</Button>
			<Modal show={isModalOpen} onHide={onHide} fullscreen>
				<Modal.Header
					style={{
						padding: '3rem',
						justifyContent: 'space-between',
					}}
				>
					<Modal.Title>
						<h2
							style={{
								fontSize: '40px',
								fontWeight: 700,
								marginBottom: '1rem',
							}}
						>
							INFORMATIVA PRIVACY CLIENTI SONAR® (SaaS)
						</h2>
						<h3
							style={{
								fontSize: '18px',
								fontWeight: 700,
							}}
						>
							(ai sensi dell'articolo 13 del Regolamento (UE) 2016/679 - “GDPR”)
						</h3>
					</Modal.Title>
					<Button
						className="btn custom-ghost-button"
						onClick={onHide}
						style={{
							// color: '#06797F',
							display: 'flex',
							alignItems: 'center',
							gap: '0.75rem',
						}}
					>
						Chiudi
						<IcoClose
						// style={{
						// 	fill: '#06797F',
						// }}
						/>
					</Button>
				</Modal.Header>
				<Modal.Body
					style={{
						padding: '3rem',
					}}
				>
					{/* Privacy Policy content goes here */}
					<div
						className="privacy_policy-content"
						style={{
							maxWidth: '1440px',
							margin: '0 auto',
							backgroundColor: '#fff',
							borderRadius: '12px',
							padding: '3rem',
						}}
					>
						<p>
							OpenEconomics S.r.l. (di seguito, “OpenEconomics” o “Società”) riconosce l'importanza di proteggere i dati personali trattati
							nell'ambito dell'erogazione e della gestione dei servizi offerti attraverso la piattaforma SaaS Sonar®, dedicata al supporto
							delle attività di gestione e rendicontazione dei progetti finanziati con fondi pubblici o privati.
						</p>
						<p>
							La presente informativa è resa ai Clienti che acquistano licenze per l'utilizzo della piattaforma Sonar®, nonché agli Utenti
							da essi autorizzati ad accedervi (di seguito, congiuntamente, gli “Interessati”).
						</p>
						<h4>1. Titolare del trattamento e Responsabile della protezione dei dati personali</h4>
						<p>
							Il Titolare del trattamento di tali dati personali è OpenEconomics S.r.l. con sede legale in Via Vitorchiano n°123, 00189 -
							Roma (RM); P.IVA 12504821005, e-mail <a href="mailto:privacy@openeconomics.eu">privacy@openeconomics.eu</a> (di seguito anche
							“Titolare” o “Società”).
						</p>
						<p>
							Il Titolare ha provveduto a nominare un Responsabile della Protezione dei dati personali (DPO), contattabile all'indirizzo
							e-mail <a href="mailto:dpo@openeconomics.eu">dpo@openeconomics.eu</a>.
						</p>
						<h4>2. Categorie di dati personali</h4>
						<p>Nell'ambito dell'utilizzo di Sonar®, OpenEconomics può trattare le seguenti tipologie di dati personali:</p>

						<ul>
							<li>
								<span>a) Dati anagrafici e di contatto:</span> nominativi, qualifiche, recapiti e-mail e telefonici di clienti e utenti
								autorizzati;
							</li>
							<li>
								<span>b) Dati di autenticazione e credenziali:</span> username, password, profili e ruoli assegnati nella piattaforma;
							</li>
							<li>
								<span>c) Log e tracciamenti di utilizzo:</span> dati relativi agli accessi, operazioni effettuate, caricamenti e modifiche
								sui documenti, messaggi di sistema e segnalazioni di errore;
							</li>
							<li>
								<span>d) Dati contrattuali e amministrativi:</span> dati di fatturazione, pagamenti e rapporti giuridici con i Clienti;
							</li>
							<li>
								<span>e) Dati contenuti nei documenti caricati sulla piattaforma</span> (che possono includere dati personali di terzi,
								inseriti dai Clienti per finalità di rendicontazione o conformità ai bandi di finanziamento);
							</li>
							<li>
								<span>f) Ticket di assistenza e help desk:</span> dati forniti spontaneamente dal Cliente o dagli utenti nelle richieste di
								supporto tecnico.
							</li>
							<li>
								<span>g) Dati di accesso via OTP</span> (One Time Password) per autenticazione sicura.
							</li>
						</ul>

						<p>
							OpenEconomics non richiede né intende trattare categorie particolari di dati personali ai sensi dell'art. 9 GDPR (es. dati
							sanitari, dati relativi alle opinioni politiche, convinzioni religiose o appartenenza sindacale). Qualora il Cliente o gli
							utenti da esso autorizzati inseriscano tali informazioni nei documenti caricati sulla piattaforma, il relativo trattamento
							rimane sotto la loro esclusiva responsabilità.
						</p>
						<h4>3. FINALITÀ DEL TRATTAMENTO</h4>
						<p>I dati personali sono trattati da OpenEconomics per le seguenti finalità:</p>

						<ul>
							<li>
								<span>a) Finalità contrattuali:</span> attivazione e gestione dell'account cliente e degli utenti autorizzati,
								configurazione iniziale della piattaforma, attivazione licenze, fatturazione e gestione amministrativa;
							</li>
							<li>
								<span>b) Esecuzione dei servizi SaaS:</span> garantire le funzionalità di Sonar® (upload e gestione documentale, sistemi di
								checklist, autocontrollo, tracciabilità delle attività, gestione ruoli e permessi);
							</li>
							<li>
								<span>c) Assistenza tecnica e manutenzione:</span> erogazione di supporto (help desk, ticketing, manutenzione correttiva ed
								evolutiva), gestione anomalie e segnalazioni;
							</li>
							<li>
								<span>d) Sicurezza e accountability:</span> monitoraggio e logging delle attività per garantire integrità dei dati,
								prevenzione accessi non autorizzati, rilevazione anomalie e verifica della compliance;
							</li>
							<li>
								<span>e) Tutela dei diritti del Titolare:</span> gestione di reclami, contenziosi e recupero crediti;
							</li>
							<li>
								<span>f) Obblighi di legge:</span> adempimento a obblighi normativi in materia fiscale, contabile, societaria e regolatoria
								connessa ai programmi di finanziamento;
							</li>
							<li>
								<span>g) Comunicazioni informative e promozionali (cd. soft spam):</span> invio tramite e-mail di aggiornamenti relativi a
								servizi e prodotti analoghi a Sonar® e nuove funzionalità dei prodotti acquistati, ai sensi dell'art. 130, comma 4, del
								D.lgs. 196/2003, fermo restando il diritto dell'interessato di opporsi in ogni momento.
							</li>
						</ul>
						<h4>4. BASE GIURIDICA DEL TRATTAMENTO</h4>
						<ul
							style={{
								listStyle: 'initial',
								marginLeft: '1rem',
							}}
						>
							<li>
								L'esecuzione del Contratto (art. 6, par. 1, lett. b) GDPR) costituisce base giuridica per i trattamenti di cui ai punti a),
								b), c);
							</li>
							<li>L'adempimento ad obblighi legali (art. 6, par. 1, lett. c) GDPR) per la finalità f);</li>
							<li>
								Il legittimo interesse del Titolare (art. 6, par. 1, lett. f) GDPR) per le finalità d) ed e), limitatamente a quanto
								necessario per garantire sicurezza, accountability e difesa dei propri diritti;
							</li>
							<li>
								Il legittimo interesse ex art. 130, comma 4, Codice Privacy per l'invio di comunicazioni relative a servizi analoghi
								(finalità g).
							</li>
						</ul>
						<h4>5. MODALITÀ DEL TRATTAMENTO</h4>
						<p>
							I dati saranno trattati da OpenEconomics con sistemi elettronici e manuali secondo i principi di correttezza, lealtà e
							trasparenza previsti dalla normativa applicabile in materia di protezione dei dati personali e tutelando la riservatezza
							dell'Interessato tramite misure di sicurezza tecniche e organizzative per garantire un livello di sicurezza adeguato.
							<br />I dati sono trattati mediante infrastrutture cloud sicure, con autenticazione OTP e connessioni cifrate ospitate in data
							center conformi ai requisiti di sicurezza europei.
						</p>
						<h4>6. CONSERVAZIONE DEI DATI</h4>
						<p>
							I dati personali raccolti per le finalità di cui al precedente paragrafo 3 saranno conservati per un periodo pari alla durata
							del Contratto e per i 10 anni successivi alla cessazione dello stesso, fatti salvi i casi in cui la conservazione per un
							periodo successivo sia richiesta per eventuali contenziosi, richieste delle autorità competenti o ai sensi della normativa
							applicabile.
						</p>
						<p>
							I log di accesso e attività in piattaforma sono conservati per un periodo massimo di 24 mesi, salvo esigenze di sicurezza o
							contenziosi;
							<br />
							I ticket di assistenza sono conservati per un massimo di 36 mesi dalla loro chiusura;
							<br />I documenti caricati dai Clienti restano disponibili per la durata del contratto, secondo le impostazioni concordate e
							rispetto a quanto previsto dai Contratti, restando a carico del Cliente la responsabilità dei dati personali ivi inseriti.
						</p>
						<h4>7. COMUNICAZIONE DEI DATI</h4>
						<p>I dati saranno trattati, nei limiti di quanto necessario, da:</p>
						<ul
							style={{
								listStyle: 'initial',
								marginLeft: '1rem',
							}}
						>
							<li>personale autorizzato e istruito di OpenEconomics;</li>

							<li>fornitori di servizi cloud, hosting e manutenzione IT, nominati responsabili esterni;</li>

							<li>consulenti legali, fiscali e amministrativi;</li>

							<li>istituti bancari per la gestione dei pagamenti;</li>

							<li>enti pubblici o autorità, se richiesto per legge o per esigenze di rendicontazione dei progetti.</li>
						</ul>
						<p>La lista aggiornata dei responsabili esterni è disponibile su richiesta al Titolare.</p>
						<h4>8. DIFFUSIONE E TRASFERIMENTO DEI DATI</h4>
						<p>
							I dati non saranno in alcun caso diffusi né saranno generalmente trasferiti fuori dall'Unione Europea.
							<br />
							Tuttavia, i dati potranno essere trattati da fornitori di servizi informatici, nella loro qualità di Responsabili del
							trattamento. Qualora gli stessi operassero al di fuori dell'Unione Europea, il trattamento avverrà secondo una delle modalità
							consentite dalla legge vigente, quali ad esempio il consenso dell'interessato, l'adozione di Clausole Standard approvate dalla
							Commissione Europea, la selezione di soggetti aderenti a programmi internazionali per la libera circolazione dei dati (es.
							DATA PRIVACY FRAMEWORK (DPF)) od operanti in Paesi considerati sicuri dalla Commissione Europea. È possibile avere maggiori
							informazioni, su richiesta, presso il Titolare ai contatti sopraindicati.
						</p>
						<h4>9. QUALI SONO I DIRITTI DELL'INTERESSATO</h4>
						<p>
							Gli interessati possono esercitare, nei limiti di legge, i diritti previsti dagli artt. 15-21 GDPR: accesso, rettifica,
							cancellazione, limitazione, opposizione, portabilità dei dati.
							<br />È sempre possibile opporsi al trattamento per finalità di soft spam.
						</p>
						<p>
							Si ricorda, tuttavia, che alcuni dei diritti di cui alla sezione che precede possono essere esercitati soltanto in circostanze
							specifiche. Laddove non sia possibile soddisfare la richiesta dell'Interessato di esercitare uno dei suddetti diritti ai sensi
							della legge in materia di riservatezza dei dati personali, sarà nostra premura comunicargli per iscritto le ragioni del nostro
							rifiuto. Ulteriori informazioni sui suddetti diritti e sulla loro applicazione possono essere fornite, su richiesta,
							contattando il titolare all'indirizzo e-mail <a href="mailto:privacy@openeconomics.eu">privacy@openeconomics.eu</a>.
						</p>
						<p>Resta ferma la possibilità di proporre reclamo all'Autorità Garante per la Protezione dei Dati Personali.</p>
						<h4>10. MODIFICHE E AGGIORNAMENTI</h4>

						<p>
							La presente informativa può essere soggetta a variazioni anche in conseguenza di eventuali modifiche e/o integrazioni
							normative. Eventuali aggiornamenti saranno resi disponibili ai Clienti tramite i consueti canali di comunicazione.
						</p>
					</div>
				</Modal.Body>
				{needsAcceptance ? (
					<Modal.Footer style={{ padding: '3rem', backgroundColor: '#fff', justifyContent: 'space-between' }}>
						<Form.Group
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: '0.5rem',
							}}
						>
							<Form.Check
								id="privacy-policy-acceptance"
								checked={isAccepted}
								onChange={(event) => {
									setIsAccepted(event.target.checked);
								}}
							/>
							<Form.Label
								htmlFor="privacy-policy-acceptance"
								style={{
									marginBottom: 0,
								}}
							>
								Ho preso visione e accetto l'Informativa sulla Privacy.
							</Form.Label>
						</Form.Group>
						<div
							style={{
								display: 'flex',
								gap: '0.5rem',
							}}
						>
							<Button
								onClick={onHide}
								className="btn custom-outline-button"
								style={{
									width: '285px',
									height: '48px',
								}}
							>
								Annulla
							</Button>
							<Button
								onClick={handleOnClick}
								disabled={!isAccepted}
								className="btn custom-primary-button"
								style={{
									width: '285px',
									height: '48px',
								}}
							>
								Conferma
							</Button>
						</div>
					</Modal.Footer>
				) : null}
			</Modal>
		</>
	);
};

export default PrivacyPolicy;
